/** UI helpers for the 4-module signal system (drawer + watchlist). */

import { resolveDisplayVerdict, displayVerdictChipStyle } from "./resolveDisplayVerdict.js";
import { rsiDisplayColor, formatRsiWithDivergence } from "./rsiDisplay.js";
import { buildTierScoreRows, fmtTierScore } from "./scoreTierDisplay.js";

export const SIGNAL_BANNER_COLORS = {
  TOP_CONFIRMED: { bg: "#ef4444", text: "#fff" },
  TOP_FORMING: { bg: "#f97316", text: "#fff" },
  WAIT: { bg: "#2a2d40", text: "#e2e8f0" },
  BOTTOM_FORMING: { bg: "#0891b2", text: "#fff" },
  BOTTOM_CONFIRMED: { bg: "#0284c7", text: "#fff" },
  SWING_LONG: { bg: "#16a34a", text: "#fff" },
};

export function getSignalBannerStyle(signal) {
  return SIGNAL_BANNER_COLORS[signal] || SIGNAL_BANNER_COLORS.WAIT;
}

const MODULE_OVERRIDE_SIGNALS = new Set([
  "SWING_LONG",
  "BOTTOM_CONFIRMED",
  "BOTTOM_FORMING",
  "TOP_CONFIRMED",
  "TOP_FORMING",
]);

export function formatModuleSignalLabel(result, opts = {}) {
  return resolveDisplayVerdict(result, opts).verdict;
}

export function getWatchlistActionUi(result, opts = {}) {
  const d = resolveDisplayVerdict(result, opts);
  const s = displayVerdictChipStyle(d.verdictStyle);
  return { text: d.actionText, color: s.color, bg: s.bg, border: s.border };
}

function CheckRow({ pass, label, detail, T }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0" }}>
      <span style={{ fontSize: 13, flexShrink: 0 }}>{pass ? "✅" : "❌"}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: pass ? T.text : T.textDim }}>{label}</div>
        {detail && <div style={{ fontSize: 10, color: T.textDim, marginTop: 2, lineHeight: 1.5 }}>{detail}</div>}
      </div>
    </div>
  );
}

function ModuleSection({ title, badge, badgeColor, children, T }) {
  return (
    <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em" }}>{title}</div>
        {badge && (
          <span style={{ fontSize: 10, fontWeight: 800, color: badgeColor, padding: "3px 8px", borderRadius: 6, background: `${badgeColor}18`, border: `1px solid ${badgeColor}44` }}>
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

export function getModuleBannerSignal(result, opts = {}) {
  const d = resolveDisplayVerdict(result, opts);
  if (d.verdictStyle === "bullish") return "SWING_LONG";
  if (d.verdictStyle === "bearish") return "TOP_CONFIRMED";
  if (d.verdictStyle === "warning") return "WAIT";
  return "WAIT";
}

export function SignalSystemBanner({ result, T, inPosition = false, activeSignal = null }) {
  const display = resolveDisplayVerdict(result, { inPosition, activeSignal: activeSignal ?? result?.activeSignal });
  const chip = displayVerdictChipStyle(display.verdictStyle);
  const strength = display.signalStrength ?? 0;
  const barPct = Math.max(0, Math.min(100, strength));

  return (
    <div style={{ flexShrink: 0, padding: "10px 14px", background: chip.bannerBg, borderBottom: `1px solid ${chip.bannerBg}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontSize: 14, fontWeight: 900, color: chip.bannerText, letterSpacing: "0.02em" }}>{display.verdict}</div>
        <div style={{ fontSize: 11, fontWeight: 700, color: chip.bannerText, opacity: 0.95 }}>Strength: {strength}%</div>
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, color: chip.bannerText, marginTop: 4, opacity: 0.9 }}>{display.subtitle}</div>
      <div style={{ marginTop: 8, height: 6, background: "rgba(0,0,0,0.2)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${barPct}%`, background: "rgba(255,255,255,0.85)", borderRadius: 3, transition: "width 0.3s" }} />
      </div>
    </div>
  );
}

export function SignalReasonsSection({ result, T, max = 6 }) {
  const reasons = Array.isArray(result?.signalReasons) ? result.signalReasons.slice(0, max) : [];
  if (!reasons.length) return null;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ width: 3, height: 16, borderRadius: 2, background: T.green, flexShrink: 0 }} />
        <div style={{ fontSize: 10, fontWeight: 800, color: T.green, letterSpacing: "0.08em" }}>WHY THIS SIGNAL</div>
      </div>
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12 }}>
        {reasons.map((r, i) => (
          <div key={i} style={{ fontSize: 11, color: T.textSub, lineHeight: 1.6, padding: "4px 0" }}>✅ {r}</div>
        ))}
      </div>
    </div>
  );
}

export function ConfluencePanel({ result, T }) {
  const c = result?.confluence;
  if (!c?.conditions?.length) {
    return (
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 900, color: T.textDim, letterSpacing: "0.1em" }}>CONFLUENCE</div>
        <div style={{ fontSize: 11, color: T.textDim, marginTop: 8 }}>Rescan to evaluate tier-1 setup confluence.</div>
      </div>
    );
  }

  const active = c.active === true && c.bonus !== 0;
  const badgeColor =
    c.bonus >= 3 ? T.green : c.bonus === 2 ? T.cyan : c.bonus < 0 ? T.red : T.textDim;
  const badge = active
    ? `${c.label || c.setupType} · ${c.bonus > 0 ? "+" : ""}${c.bonus} score`
    : `${c.label || "Confluence"} · inactive`;

  return (
    <div
      style={{
        background: active ? `${badgeColor}10` : T.bg,
        border: `2px solid ${active ? badgeColor : T.border}`,
        borderRadius: 12,
        padding: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, fontWeight: 900, color: active ? badgeColor : T.textDim, letterSpacing: "0.1em" }}>
          CONFLUENCE
        </div>
        <span
          style={{
            fontSize: 10,
            fontWeight: 800,
            color: badgeColor,
            padding: "4px 10px",
            borderRadius: 6,
            background: `${badgeColor}18`,
            border: `1px solid ${badgeColor}44`,
          }}
        >
          {badge}
        </span>
      </div>
      {c.reason && active && (
        <div style={{ fontSize: 11, color: T.textSub, marginBottom: 10, lineHeight: 1.6 }}>{c.reason}</div>
      )}
      {!active && (
        <div style={{ fontSize: 10, color: T.textDim, marginBottom: 10 }}>
          Highest-conviction template not fully aligned — {c.partialMatch ?? 0}/{c.conditions.length} conditions met ({c.label || c.setupType}).
        </div>
      )}
      {c.conditions.map((row) => (
        <CheckRow
          key={row.key || row.label}
          pass={row.pass === true}
          label={row.label}
          detail={row.detail}
          T={T}
        />
      ))}
    </div>
  );
}

export function PriceStorySection({ result, T }) {
  const rawStory = result?.priceStoryParagraph;
  const story = typeof rawStory === "string" ? rawStory : rawStory != null ? String(rawStory) : "";
  const dcb = result?.deadCatBounce;
  if (!story && !dcb) return null;
  return (
    <div style={{ padding: "14px 16px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 10 }}>📖 PRICE STORY</div>
      {story ? (
        <div style={{ fontSize: 12, color: T.text, lineHeight: 1.85, whiteSpace: "pre-wrap" }}>{story}</div>
      ) : (
        <div style={{ fontSize: 12, color: T.textDim }}>No price story for this symbol.</div>
      )}
    </div>
  );
}

export function EntryGateChecklist({ result, T }) {
  const gate = result?.gate;
  if (!gate?.checks?.length) return null;
  const gateColor = gate.label === "GO" ? T.green : gate.label === "CLOSE" ? T.yellow : T.orange;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ width: 3, height: 16, borderRadius: 2, background: gateColor, flexShrink: 0 }} />
        <div style={{ fontSize: 10, fontWeight: 800, color: gateColor, letterSpacing: "0.08em" }}>ENTRY GATE — {gate.label}</div>
        <div style={{ fontSize: 10, color: T.textDim, opacity: 0.6 }}>· {gate.passed}/{gate.total} conditions</div>
      </div>
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
        {gate.checks.map((c, i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.pass ? T.green : c.critical ? T.red : T.yellow, marginTop: 4, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: c.pass ? T.textSub : c.critical ? T.red : T.yellow, fontWeight: c.critical && !c.pass ? 700 : 400 }}>{c.label}</div>
              {!c.pass && c.why && <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>{c.why}</div>}
            </div>
            {c.critical && !c.pass && (
              <div style={{ fontSize: 9, fontWeight: 800, color: T.red, flexShrink: 0, padding: "1px 5px", background: `${T.red}18`, borderRadius: 4 }}>CRITICAL</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function TierScoreBreakdownPanel({ result, T }) {
  const tier = buildTierScoreRows(result);
  if (!tier) return null;
  const mono = { fontFamily: "'JetBrains Mono', monospace" };
  const scoreColor = (v) => (v > 0 ? T.green : v < 0 ? T.red : T.textDim);
  const capped = tier.capped;
  const showCapNote = typeof capped === "number" && tier.rawTotal !== capped;

  return (
    <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px 16px" }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 12 }}>SCORE BREAKDOWN BY TIER</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {tier.rows.map((row) => (
          <div key={row.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, fontSize: 12 }}>
            <span style={{ color: T.textSub }}>{row.label}</span>
            <span style={{ ...mono, fontWeight: 800, color: scoreColor(row.score) }}>{fmtTierScore(row.score)}</span>
          </div>
        ))}
        <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 6, paddingTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: T.text }}>Total</span>
          <span style={{ ...mono, fontSize: 13, fontWeight: 900, color: scoreColor(tier.rawTotal) }}>
            {fmtTierScore(tier.rawTotal)}
            {showCapNote && typeof capped === "number" && (
              <span style={{ fontSize: 11, fontWeight: 700, color: T.textDim }}> → capped at {fmtTierScore(capped)}</span>
            )}
          </span>
        </div>
        {showCapNote && (
          <div style={{ fontSize: 10, color: T.textDim, lineHeight: 1.5 }}>
            Tier subtotals highlight structure, flow, and confluence; final score also includes MACD, RS vs SPY, phase, and market tide (±15 cap).
          </div>
        )}
      </div>
    </div>
  );
}

/** Drawer quick context — entry gate only (full analysis lives on Analysis tab). */
export function DrawerOverviewStack({ result, T }) {
  if (!result) return null;
  return (
    <div style={{ padding: "12px 14px", borderBottom: `1px solid ${T.border}`, background: T.surface }}>
      <EntryGateChecklist result={result} T={T} />
    </div>
  );
}

export function ModuleAnalysisPanel({ result, T }) {
  const m1 = result?.module1 || {};
  const m2 = result?.module2 || {};
  const m3 = result?.module3 || {};
  const price = result?.price;
  const sm50 = result?.sm50;
  const sm200 = result?.sm200;
  const ema20 = result?.ema20?.value;
  const cmf = result?.cmf;
  const rsi = result?.rsi;
  const rvol = result?.rvol?.ratio;
  const obvRising = result?.obv?.rising || result?.module1?.validation?.obv;
  const rsTrend = result?.relativeStrength?.rs_line_trend;
  const rsSlope = rsTrend === "RISING" ? "+" : rsTrend === "FALLING" ? "−" : "~";

  const m1Badge = m1.signal === "SWING_LONG"
    ? { text: "SWING TRADE: ✅ SIGNAL ACTIVE", color: T.green }
    : { text: "SWING TRADE: ❌ No Signal", color: T.textDim };

  const m2Badge = m2.signal === "BOTTOM_CONFIRMED"
    ? { text: "BOTTOM: ✅ CONFIRMED", color: "#0284c7" }
    : m2.signal === "BOTTOM_FORMING"
    ? { text: "BOTTOM: 👁 FORMING", color: "#0891b2" }
    : { text: "BOTTOM: ❌ Not Detected", color: T.textDim };

  const m3Badge = m3.signal === "TOP_CONFIRMED"
    ? { text: "TOP: 🔴 CONFIRMED", color: T.red }
    : m3.signal === "TOP_FORMING"
    ? { text: "TOP: ⚠ FORMING", color: T.orange }
    : { text: "TOP: ❌ Not Detected", color: T.textDim };

  const trendPass = price != null && sm50 != null && sm200 != null && price > sm50 && sm50 > sm200;
  const rsPass = result?.module1?.preFilterPass != null
    ? (m1.preFilterPass ? trendPass : false) || rsTrend === "RISING"
    : rsTrend === "RISING";

  const rsiDiv = result?.rsiDiv;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <ModuleSection title="RSI (14) — SCORE & DIVERGENCE" badge={rsi != null ? formatRsiWithDivergence(rsi, rsiDiv, 0) : "—"} badgeColor={rsi != null ? rsiDisplayColor(rsi, T) : T.textDim} T={T}>
        <CheckRow pass={rsi != null && rsi > 70} label={rsi != null && rsi > 70 ? "Strong momentum (RSI > 70) — no score penalty" : rsi != null ? `RSI ${rsi.toFixed(0)}` : "RSI —"} T={T}
          detail="Midline scoring: cross above 50 +2, above 50 rising +1, below 50 falling −1, cross below 50 −2, oversold <30 +1." />
        <CheckRow pass={!!rsiDiv?.bullish} label={rsiDiv?.bullish ? "✅ Bullish divergence" : "Bullish divergence"} T={T}
          detail={rsiDiv?.bullish ? "Price lower low, RSI higher low (20 bars) — +2 score" : "Not detected"} />
        <CheckRow pass={!!rsiDiv?.bearish} label={rsiDiv?.bearish ? "⚠ Bearish divergence" : "Bearish divergence"} T={T}
          detail={rsiDiv?.bearish ? "Price higher high, RSI lower high (20 bars) — −2 score" : "Not detected"} />
      </ModuleSection>

      <ModuleSection title="MODULE 1 — SWING TRADE ENGINE" badge={m1Badge.text} badgeColor={m1Badge.color} T={T}>
        <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, marginBottom: 6 }}>PRE-FILTERS</div>
        <CheckRow pass={trendPass} label="Trend Structure (Price > SMA50 > SMA200)" T={T}
          detail={sm50 != null ? `Price $${price?.toFixed(2)} | SMA50 $${sm50.toFixed(2)} | SMA200 $${sm200?.toFixed(2) ?? "—"}` : null} />
        <CheckRow pass={!!m1.preFilterPass && rsTrend === "RISING"} label="Relative Strength Rising" T={T}
          detail={`RS Line 20-day slope: ${rsSlope}${result?.relativeStrength?.vs_spy_21d != null ? ` (${result.relativeStrength.vs_spy_21d > 0 ? "+" : ""}${result.relativeStrength.vs_spy_21d.toFixed(1)}% vs SPY 21d)` : ""}`} />
        <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, margin: "10px 0 6px" }}>SETUP DETECTED</div>
        <CheckRow pass={!!m1.setupA} label="Pullback to Support" T={T}
          detail={m1.setupA ? `Near EMA20 $${ema20?.toFixed(2) ?? "—"} / SMA50 $${sm50?.toFixed(2) ?? "—"} · Range compression: yes` : "Range compression: no"} />
        <CheckRow pass={!!m1.setupB} label="VCP Breakout" T={T}
          detail={m1.setupB ? `Breakout confirmed · Setup: ${m1.setupType || "VCP_BREAKOUT"}` : null} />
        <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, margin: "10px 0 6px" }}>VALIDATION</div>
        <CheckRow pass={!!m1.validation?.volume} label={`Volume ${rvol != null ? `${rvol.toFixed(1)}x` : "—"} average`} T={T} detail="Need > 1.5x" />
        <CheckRow pass={!!m1.validation?.cmf} label={`CMF ${cmf != null ? cmf.toFixed(3) : "—"}`} T={T} detail="Need > 0.10" />
        <CheckRow pass={!!m1.validation?.obv} label="OBV rising on pullback" T={T} detail={obvRising ? "OBV trending up" : null} />
        <CheckRow pass={!!m1.validation?.rsi} label={`RSI ${rsi != null ? rsi.toFixed(0) : "—"}`} T={T} detail="Need 45-65" />
      </ModuleSection>

      <ModuleSection title="MODULE 2 — BOTTOM DETECTION" badge={m2Badge.text} badgeColor={m2Badge.color} T={T}>
        <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, marginBottom: 6 }}>PATTERNS</div>
        <CheckRow pass={!!m2.setupA?.active || !!m2.setupA?.triggered} label="Double Bottom (W-Pattern)" T={T}
          detail={m2.setupA?.triggered ? "Neckline break — pattern confirmed" : m2.setupA?.active ? "Pattern forming — watch neckline" : null} />
        <CheckRow pass={!!m2.setupB?.active || !!m2.setupB?.triggered} label="Higher Low Confirmed" T={T}
          detail={m2.setupB?.triggered ? "Higher low breakout active" : m2.setupB?.active ? "Higher low structure developing" : null} />
        <CheckRow pass={!!m2.setupC?.active} label="Hammer/Tail at Support" T={T}
          detail={m2.setupC?.active ? "Hammer candle at support zone" : null} />
        <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, margin: "10px 0 6px" }}>SUPPLY DYNAMICS</div>
        <CheckRow pass={!!m2.capitulationVolume} label="Capitulation Volume at Low 1" T={T} detail={m2.capitulationVolume ? "Volume spike on first low" : null} />
        <CheckRow pass={!!m2.exhaustionRetest} label="Supply Exhaustion at Low 2" T={T} detail={m2.exhaustionRetest ? "Lower volume on retest" : null} />
        <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, margin: "10px 0 6px" }}>MOMENTUM CONFIRMATION</div>
        <CheckRow pass={!!m2.rsiBullishDivergence} label="RSI Bullish Divergence" T={T}
          detail={m2.rsiBullishDivergence ? "RSI higher low while price made lower low" : null} />
        <CheckRow pass={!!m2.obvStabilization} label="OBV Stabilization" T={T}
          detail={m2.obvStabilization ? "OBV flat/rising while price made lower low" : null} />
      </ModuleSection>

      <ModuleSection title="MODULE 3 — TOP CONFIRMATION" badge={m3Badge.text} badgeColor={m3Badge.color} T={T}>
        <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, marginBottom: 6 }}>PATTERNS</div>
        <CheckRow pass={!!m3.setupA?.active || !!m3.setupA?.triggered} label="Double Top (M-Pattern)" T={T}
          detail={m3.setupA?.triggered ? "Neckline break down — top confirmed" : m3.setupA?.active ? "Double top forming" : null} />
        <CheckRow pass={!!m3.setupB?.active || !!m3.setupB?.triggered} label="Lower High Confirmed" T={T}
          detail={m3.setupB?.triggered ? "Lower high breakdown active" : m3.setupB?.active ? "Lower high structure developing" : null} />
        <CheckRow pass={!!m3.setupC?.active} label="Shooting Star at Resistance" T={T}
          detail={m3.setupC?.active ? "Rejection candle at resistance" : null} />
        <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, margin: "10px 0 6px" }}>DISTRIBUTION DYNAMICS</div>
        <CheckRow pass={!!m3.distributionVolume} label="Distribution Volume at High 1" T={T}
          detail={m3.distributionVolume ? "High volume, closed lower half" : null} />
        <CheckRow pass={!!m3.exhaustionRetest} label="Exhaustion at High 2 Retest" T={T}
          detail={m3.exhaustionRetest ? "Lower volume on second top" : null} />
        <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, margin: "10px 0 6px" }}>MOMENTUM CONFIRMATION</div>
        <CheckRow pass={!!m3.rsiBearishDivergence} label="RSI Bearish Divergence" T={T}
          detail={m3.rsiBearishDivergence ? "RSI lower high while price made higher high" : null} />
        <CheckRow pass={!!m3.obvDistribution} label="OBV Distribution" T={T}
          detail={m3.obvDistribution ? "OBV declining while price at highs" : null} />
        <CheckRow pass={!!m3.cmfDistribution} label="CMF Negative at Highs" T={T}
          detail={m3.cmfDistribution ? `CMF ${cmf != null ? cmf.toFixed(3) : "—"} — institutions selling into strength` : null} />
      </ModuleSection>
    </div>
  );
}

export function OrderFlowSummary({ result, T }) {
  const cmf = result?.cmf;
  const rvol = result?.rvol?.ratio;
  const obvUp = result?.obv?.rising || result?.module1?.validation?.obv;
  const closes = result?.closes;
  const opens = result?.opens;
  const volumes = result?.volumes;

  let upDays = 0;
  let downDays = 0;
  let upVol = 0;
  let downVol = 0;
  if (closes?.length >= 10) {
    for (let i = 0; i < 10; i++) {
      const o = opens?.[i] ?? closes[i + 1];
      const c = closes[i];
      const v = volumes?.[i] || 0;
      if (c > o) { upDays++; upVol += v; }
      else if (c < o) { downDays++; downVol += v; }
    }
  }
  const buyerPressure = upDays > downDays && upVol > downVol ? "HIGH" : upDays >= downDays || upVol >= downVol ? "MEDIUM" : "LOW";
  const netBias = cmf != null && cmf > 0.05 ? "BULLISH" : cmf != null && cmf < -0.05 ? "BEARISH" : "NEUTRAL";
  const cmfLabel = cmf != null
    ? (cmf > 0.05 ? "↑ Accumulation" : cmf < -0.05 ? "↓ Distribution" : "~ Neutral")
    : "—";

  return (
    <div style={{ marginBottom: 14, padding: "12px 14px", background: T.bg, border: `1px solid ${T.cyan}44`, borderRadius: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: T.cyan, letterSpacing: "0.08em", marginBottom: 10 }}>ORDER FLOW SUMMARY</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 11 }}>
        <div><span style={{ color: T.textDim }}>CMF:</span> <span style={{ color: cmf > 0 ? T.green : cmf < 0 ? T.red : T.text, fontWeight: 700 }}>{cmf != null ? `${cmf >= 0 ? "+" : ""}${cmf.toFixed(2)} ${cmfLabel}` : "—"}</span></div>
        <div><span style={{ color: T.textDim }}>OBV:</span> <span style={{ color: obvUp ? T.green : T.textSub, fontWeight: 700 }}>{obvUp ? "Rising ✅" : "Flat/Falling"}</span></div>
        <div><span style={{ color: T.textDim }}>Volume:</span> <span style={{ fontWeight: 700, color: rvol >= 1.5 ? T.green : T.text }}>{rvol != null ? `${rvol.toFixed(1)}x average` : "—"}{rvol >= 1.5 ? " — Expansion" : ""}</span></div>
        <div><span style={{ color: T.textDim }}>Buyer Pressure:</span> <span style={{ fontWeight: 700, color: buyerPressure === "HIGH" ? T.green : buyerPressure === "LOW" ? T.red : T.yellow }}>{buyerPressure}</span></div>
      </div>
      <div style={{ marginTop: 8, fontSize: 11, fontWeight: 700, color: netBias === "BULLISH" ? T.green : netBias === "BEARISH" ? T.red : T.textSub }}>
        Net Bias: {netBias}
      </div>
    </div>
  );
}

export function Module4RecommendationCard({ result, T }) {
  const m4 = result?.module4;
  if (!m4?.strategy || m4.strategy === "WAIT") {
    return (
      <div style={{ padding: "12px 14px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, marginBottom: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 8 }}>MODULE 4 — OPTIONS RECOMMENDATION</div>
        <div style={{ fontSize: 11, color: T.textDim }}>No directional edge — wait for module signal</div>
      </div>
    );
  }
  return (
    <div style={{ padding: "12px 14px", background: T.bg, border: `1px solid ${T.purple}44`, borderRadius: 10, marginBottom: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: T.purple, letterSpacing: "0.08em", marginBottom: 10 }}>MODULE 4 — OPTIONS RECOMMENDATION</div>
      <div style={{ fontSize: 12, fontWeight: 800, color: T.text, marginBottom: 6 }}>Strategy: {m4.strategy.replace(/_/g, " ")}</div>
      {m4.dte && <div style={{ fontSize: 11, color: T.textSub, marginBottom: 4 }}>DTE: {m4.dte}</div>}
      {m4.strike && <div style={{ fontSize: 11, color: T.textSub, marginBottom: 4 }}>Strike: {m4.strike}</div>}
      {m4.reason && <div style={{ fontSize: 11, color: T.textDim, lineHeight: 1.5, marginTop: 6 }}>Why: {m4.reason}</div>}
      {m4.earningsWarning && (
        <div style={{ marginTop: 10, padding: "8px 10px", background: `${T.orange}18`, border: `1px solid ${T.orange}44`, borderRadius: 8, fontSize: 11, color: T.orange, fontWeight: 700 }}>
          {m4.earningsWarning}
          {m4.suggestSpreads && <div style={{ fontWeight: 500, marginTop: 4, color: T.textSub }}>Consider defined risk spread</div>}
        </div>
      )}
    </div>
  );
}

const OPTIONS_MECHANICS = {
  LONG_CALL: `You pay premium upfront.
Max loss: premium paid (100% of investment)
Profit if: stock rises above strike + premium paid before expiry
Time decay: works against you daily
Best exit: when signal reverses OR at 100-150% gain on the option`,
  LONG_PUT: `You pay premium upfront.
Max loss: premium paid
Profit if: stock falls below strike - premium paid before expiry
Time decay: works against you daily
⚠ Puts decay faster — don't overstay`,
  BULL_PUT_SPREAD: `You collect premium upfront.
Max gain: premium collected
Max loss: spread width - premium
Profit if: stock stays above short strike
Time decay: works FOR you daily`,
  BEAR_CALL_SPREAD: `You collect premium upfront.
Max gain: premium collected
Max loss: spread width - premium
Profit if: stock stays below short strike
Time decay: works FOR you daily`,
};

const STRATEGY_HEADERS = {
  LONG_CALL: "📞 LONG CALL",
  LONG_PUT: "📉 LONG PUT",
  BULL_PUT_SPREAD: "📊 BULL PUT SPREAD",
  BEAR_CALL_SPREAD: "📊 BEAR CALL SPREAD",
};

export function OptionsSignalPanel({ result, T }) {
  const m4 = result?.module4 || {};
  const strategy = m4.strategy || "WAIT";
  const header = STRATEGY_HEADERS[strategy] || strategy.replace(/_/g, " ");
  const mechanics = OPTIONS_MECHANICS[strategy] || "Select a directional module signal for options guidance.";
  const ivRank = result?.bb?.ivRank;
  const ivLabel = ivRank != null
    ? `IV Rank: ${ivRank}% — ${ivRank <= 35 ? "cheap" : ivRank >= 65 ? "elevated" : "average"}`
    : "Connect Tradier for IV data";
  const earnDays = result?.earningsDaysAway ?? result?.scanEarningsDaysAway ?? result?.earningsState?.daysAway;
  const timeStop = m4.dte?.includes("60") ? 45 : 21;

  return (
    <div style={{ padding: "16px 16px", display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 8 }}>SECTION 1 — RECOMMENDED PLAY</div>
        <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: T.text, marginBottom: 10 }}>{header}</div>
          <div style={{ fontSize: 11, color: T.textSub, lineHeight: 1.8 }}>
            <div><strong>Expiration:</strong> {m4.dte || "—"}</div>
            <div><strong>Strike:</strong> {m4.strike || "—"}</div>
            <div><strong>Signal basis:</strong> {formatModuleSignalLabel(result)}</div>
            <div><strong>IV context:</strong> {ivLabel}</div>
            {m4.reason && <div style={{ marginTop: 6, color: T.textDim }}>{m4.reason}</div>}
          </div>
        </div>
      </div>

      <div>
        <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 8 }}>SECTION 2 — TRADE MECHANICS</div>
        <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, fontSize: 11, color: T.textSub, lineHeight: 1.75, whiteSpace: "pre-line" }}>
          {mechanics}
        </div>
      </div>

      {typeof earnDays === "number" && earnDays <= 45 && earnDays >= 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.orange, letterSpacing: "0.08em", marginBottom: 8 }}>SECTION 3 — EARNINGS IMPACT</div>
          <div style={{ background: `${T.orange}12`, border: `1px solid ${T.orange}44`, borderRadius: 10, padding: 14, fontSize: 11, color: T.textSub, lineHeight: 1.75 }}>
            ⚠ EARNINGS IN {earnDays} DAYS<br />
            Options get expensive before earnings (IV expansion). After earnings, options lose value fast (IV crush) even if the stock moves correctly.<br />
            <strong>Recommendation:</strong> {m4.suggestSpreads ? "Use defined-risk spreads or close before earnings" : earnDays <= 14 ? "Close before earnings or use spreads" : "Monitor IV — consider closing 7 days before report"}
          </div>
        </div>
      )}

      <div>
        <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 8 }}>SECTION 4 — WHEN TO EXIT</div>
        <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, fontSize: 11, color: T.textSub, lineHeight: 1.9 }}>
          EXIT THE OPTIONS POSITION WHEN:<br />
          ✅ Profit target hit (100%+ gain)<br />
          ✅ Primary signal reverses<br />
          ✅ Stop price hit on underlying<br />
          {typeof earnDays === "number" && earnDays <= 45 && earnDays >= 7 && <>✅ {earnDays - 7} days before earnings<br /></>}
          ⏱ Time stop: {timeStop} days from entry
        </div>
      </div>
    </div>
  );
}
